/**
 * Landing Page Component
 * Main marketing page for unauthenticated users
 * Displays hero, features, FAQ, and beta signup
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../common/Button';
import IconDaggerheart from '../common/IconDaggerheart';
import { parseFaqMarkdown } from '../../utils/parseFaqMarkdown';
import toast from 'react-hot-toast';

// Screenshot data for the carousel
const SCREENSHOTS = [
  {
    src: '/images/screenshots/character-sheet.png',
    alt: 'Character Sheet with dice roller and domain cards',
    title: 'Interactive Character Sheet',
    description: 'Dice roller, domain cards, and live stats',
  },
  {
    src: '/images/screenshots/campaign-dashboard.png',
    alt: 'GM Dashboard with session notes and party roster',
    title: 'GM Dashboard',
    description: 'Session notes, DC queue, and live party tracking',
  },
  {
    src: '/images/screenshots/beast-feast.png',
    alt: 'Beast Feast cooking minigame with meal benefits',
    title: 'Campaign Frames',
    description: 'Beast Feast cooking with dice-matching minigame',
  },
];

// Feature data for the showcase section
const FEATURES = [
  {
    title: 'Every Rule, Interactive',
    description: '189 domain cards with tap-to-activate abilities. Every ancestry, community, and subclass from the SRD — not a static PDF, a living character sheet.',
    icon: '⚔️',
  },
  {
    title: 'Real-Time Campaigns',
    description: 'GM dashboard with live HP, stress, and dice rolls. Session notes your whole party can access. Player presence tracking so you always know who is at the table.',
    icon: '👥',
  },
  {
    title: 'Campaign Chronicler',
    description: 'Record your Discord session and get structured notes with GM and player perspectives, speaker identification, and a "Previously On..." recap. Hours of post-session homework, handled in seconds.',
    icon: '📜',
  },
  {
    title: '5 Campaign Frames, 14 Mechanics',
    description: 'Beast Feast cooking, Witherwild corruption, faction diplomacy, day/night cycles — 14 interactive mechanics across 5 campaign frames that reshape your game.',
    icon: '🎭',
  },
  {
    title: 'Mobile-First Design',
    description: 'Built for phones and tablets from day one. Full character sheet, dice roller, and campaign tools optimized for the table.',
    icon: '📱',
  },
  {
    title: 'Homebrew Workshop',
    description: 'Build custom abilities and domain cards with a guided creator, live preview, and built-in balance checking. Your creations work just like SRD content in the character sheet.',
    icon: '🔮',
  },
];

// Screenshot Carousel Component
function ScreenshotCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  // Auto-advance every 5 seconds
  useEffect(() => {
    if (isPaused || lightboxOpen) return;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % SCREENSHOTS.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [isPaused, lightboxOpen]);

  const goToSlide = useCallback((index) => {
    setCurrentIndex(index);
  }, []);

  const current = SCREENSHOTS[currentIndex];

  return (
    <>
      <div
        className="relative"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Main carousel container - fixed 16:9 aspect ratio */}
        <div
          className="relative aspect-video overflow-hidden rounded-lg border border-gray-700 hover:border-red-600 transition-colors cursor-pointer"
          onClick={() => setLightboxOpen(true)}
        >
          <img
            src={current.src}
            alt={current.alt}
            loading="lazy"
            className="w-full h-full object-cover object-top transition-opacity duration-500"
          />
          {/* Overlay with title */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-6">
            <h3 className="font-bold text-white text-xl">{current.title}</h3>
            <p className="text-gray-300">{current.description}</p>
          </div>
          {/* Click hint */}
          <div className="absolute top-4 right-4 bg-black/60 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
            Click to expand
          </div>
        </div>

        {/* Navigation arrows */}
        <button
          onClick={(e) => { e.stopPropagation(); setCurrentIndex((prev) => (prev - 1 + SCREENSHOTS.length) % SCREENSHOTS.length); }}
          className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white w-10 h-10 rounded-full flex items-center justify-center transition-colors"
          aria-label="Previous screenshot"
        >
          ‹
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); setCurrentIndex((prev) => (prev + 1) % SCREENSHOTS.length); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white w-10 h-10 rounded-full flex items-center justify-center transition-colors"
          aria-label="Next screenshot"
        >
          ›
        </button>

        {/* Dot indicators */}
        <div className="flex justify-center gap-2 mt-4">
          {SCREENSHOTS.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2.5 h-2.5 rounded-full transition-colors ${
                index === currentIndex ? 'bg-red-500' : 'bg-gray-600 hover:bg-gray-500'
              }`}
              aria-label={`Go to screenshot ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            className="absolute top-4 right-4 text-white text-3xl hover:text-red-400 transition-colors"
            onClick={() => setLightboxOpen(false)}
            aria-label="Close lightbox"
          >
            ×
          </button>
          <img
            src={current.src}
            alt={current.alt}
            loading="lazy"
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          {/* Lightbox navigation */}
          <button
            onClick={(e) => { e.stopPropagation(); setCurrentIndex((prev) => (prev - 1 + SCREENSHOTS.length) % SCREENSHOTS.length); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white w-12 h-12 rounded-full flex items-center justify-center text-2xl transition-colors"
            aria-label="Previous screenshot"
          >
            ‹
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setCurrentIndex((prev) => (prev + 1) % SCREENSHOTS.length); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white w-12 h-12 rounded-full flex items-center justify-center text-2xl transition-colors"
            aria-label="Next screenshot"
          >
            ›
          </button>
          {/* Caption in lightbox */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-center">
            <h3 className="font-bold text-white text-xl">{current.title}</h3>
            <p className="text-gray-300">{current.description}</p>
          </div>
        </div>
      )}
    </>
  );
}

// FAQ Accordion Item
function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-gray-700/50 last:border-b-0 px-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-4 flex justify-between items-center text-left hover:text-red-400 transition-all duration-200"
      >
        <span className="text-gray-100 font-medium pr-4">{question}</span>
        <span className={`transition-all duration-200 ${isOpen ? 'rotate-180 text-red-400' : 'text-gray-400'}`}>
          ▼
        </span>
      </button>
      {isOpen && (
        <div className="pb-4 text-gray-300 text-sm leading-relaxed">
          {answer}
        </div>
      )}
    </div>
  );
}

// Main Landing Page Component
export default function LandingPage() {
  const { loginWithGoogle } = useAuth();
  const [loginLoading, setLoginLoading] = useState(false);
  const [faqItems, setFaqItems] = useState([]);

  useEffect(() => {
    fetch('/data/faq-content.md')
      .then((res) => res.text())
      .then((md) => setFaqItems(parseFaqMarkdown(md)))
      .catch((err) => console.error('Failed to load FAQ:', err));
  }, []);

  const handleLogin = async () => {
    try {
      setLoginLoading(true);
      await loginWithGoogle();
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Failed to sign in. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  // Group FAQs by category
  const faqByCategory = faqItems.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-surface-1 text-gray-100">
      {/* Navigation — matches AppHeader */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-surface-1 p-4 flex items-center justify-between h-[73px]">
        <div className="flex items-center space-x-3">
          <IconDaggerheart className="w-8 h-8 text-red-400" />
          <span className="text-2xl font-bold text-gray-100 tracking-wider font-serif">
            Chaggerheart
          </span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleLogin}
          disabled={loginLoading}
        >
          {loginLoading ? 'Signing in...' : 'Sign In'}
        </Button>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-block px-4 py-1 bg-red-900/30 border border-red-700 rounded-full text-red-400 text-sm mb-6">
            Now in Open Beta
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 leading-tight">
            The Daggerheart Companion That Lets You Just Play
          </h1>
          <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
            Character creation, live gameplay, and session management in one place. No more PDFs, spreadsheets, or post-session homework.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="primary" size="xl" onClick={handleLogin} disabled={loginLoading}>
              {loginLoading ? 'Loading...' : 'Join the Beta'}
            </Button>
            <Button variant="outline" size="xl" onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}>
              See Features
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Why Chaggerheart?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {FEATURES.map((feature) => (
              <div
                key={feature.title}
                className="bg-gradient-to-br from-gray-900 to-black p-6 rounded-lg border border-red-700 hover:border-red-600 shadow-2xl transition-all duration-200 relative"
              >
                {feature.coming && (
                  <span className="absolute top-3 right-3 text-[10px] font-bold text-red-400 bg-red-900/30 border border-red-700 rounded-full px-2 py-0.5">
                    COMING SOON
                  </span>
                )}
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Screenshot Carousel */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">See It in Action</h2>
          <ScreenshotCarousel />
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
          {Object.keys(faqByCategory).length > 0 ? (
            <div className="space-y-6">
              {Object.entries(faqByCategory).map(([category, items]) => (
                <div key={category}>
                  <h3 className="text-lg font-bold text-red-400 mb-3">{category}</h3>
                  <div className="bg-gradient-to-br from-gray-900 to-black rounded-lg border border-red-700 divide-y divide-gray-700/50 shadow-2xl">
                    {items.map((item) => (
                      <FAQItem key={item.question} {...item} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">Loading FAQ...</div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Join?</h2>
          <p className="text-gray-400 mb-8">
            Join the open beta and get free access to all features. No payment required during beta.
          </p>
          <Button variant="primary" size="xl" onClick={handleLogin} disabled={loginLoading}>
            {loginLoading ? 'Signing in...' : 'Join the Beta'}
          </Button>
          <p className="text-xs text-gray-500 mt-4">
            By signing in, you agree to our{' '}
            <Link to="/terms" className="text-gray-400 hover:text-red-400 underline transition-colors">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="text-gray-400 hover:text-red-400 underline transition-colors">
              Privacy Policy
            </Link>
            .
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-gray-700/50">
        <div className="max-w-6xl mx-auto">
          {/* DPCGL Attribution */}
          <div className="text-center text-sm text-gray-500 mb-6 max-w-2xl mx-auto">
            <p className="mb-2">Compatible with Daggerheart.</p>
            <p>
              This application uses content from the Daggerheart System Reference Document
              under the Darrington Press Community Gaming License. Daggerheart and Darrington Press
              are trademarks of Darrington Press, LLC.
            </p>
          </div>

          {/* Links */}
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400 mb-6">
            <Link to="/about" className="hover:text-red-400 transition-colors">About</Link>
            <Link to="/privacy" className="hover:text-red-400 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-red-400 transition-colors">Terms of Service</Link>
            <a href="https://discord.gg/uYf6WCQ4z7" target="_blank" rel="noopener noreferrer" className="hover:text-red-400 transition-colors">
              Discord
            </a>
          </div>

          {/* Copyright */}
          <div className="text-center text-xs text-gray-600">
            © 2025-2026 Chag Innovations LLC. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
